import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import BookCard from "@/components/BookCard";
import { Input } from "@/components/ui/input";
import { Search } from "lucide-react";

export default function Browse() {
  const [params, setParams] = useSearchParams();
  const [books, setBooks] = useState([]);
  const [categories, setCategories] = useState([]);
  const [category, setCategory] = useState("all");
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState(params.get("filter") || "all"); // all | classic | original
  const [counts, setCounts] = useState({});

  useEffect(() => {
    api.get("/books/categories").then((r) => setCategories(["all", ...r.data])).catch(() => {});
    api.get("/purchases/counts?window=day").then((r) => setCounts(r.data.counts || {})).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const q = {};
    if (category !== "all") q.category = category;
    if (search) q.search = search;
    const t = setTimeout(() => {
      api.get("/books", { params: q }).then((r) => {
        let arr = r.data;
        if (filter === "classic") arr = arr.filter((b) => b.external_url);
        else if (filter === "original") arr = arr.filter((b) => !b.external_url);
        setBooks(arr);
      }).catch(() => {});
    }, 200);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [category, search, filter]);

  const updateFilter = (f) => {
    setFilter(f);
    if (f === "all") { params.delete("filter"); setParams(params); }
    else { params.set("filter", f); setParams(params); }
  };

  return (
    <div data-testid="browse-page" className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-24">
      <div className="mb-12">
        <div className="label-eyebrow text-sage mb-3">The full catalog</div>
        <h1 className="font-serif text-5xl sm:text-6xl tracking-tight">Browse the shelf.</h1>
      </div>

      {/* Edition filter */}
      <div className="flex gap-2 mb-8">
        {[
          { key: "all", label: "All" },
          { key: "classic", label: "Classics" },
          { key: "original", label: "Folio Originals" },
        ].map((f) => (
          <button
            key={f.key}
            data-testid={`filter-${f.key}`}
            onClick={() => updateFilter(f.key)}
            className={`text-xs uppercase tracking-widest px-4 py-2 border transition-colors ${
              filter === f.key
                ? "bg-terracotta text-paper border-terracotta"
                : "border-edge text-muted_ink hover:border-terracotta hover:text-terracotta"
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      <div className="flex flex-col md:flex-row gap-6 mb-12 border-b border-edge pb-8">
        <div className="relative flex-1 max-w-md">
          <Search className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-muted_ink" strokeWidth={1.5} />
          <Input
            data-testid="browse-search-input"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by title or author"
            className="pl-11 h-12 bg-transparent border border-edge rounded-none font-sans focus-visible:ring-terracotta focus-visible:ring-1"
          />
        </div>
        <div className="flex flex-wrap gap-2">
          {categories.map((c) => (
            <button
              key={c}
              data-testid={`category-filter-${c}`}
              onClick={() => setCategory(c)}
              className={`text-xs uppercase tracking-widest px-4 py-2 border transition-colors ${
                category === c
                  ? "bg-ink text-paper border-ink"
                  : "border-edge text-muted_ink hover:border-ink hover:text-ink"
              }`}
            >
              {c === "all" ? "All" : c}
            </button>
          ))}
        </div>
      </div>

      {books.length === 0 ? (
        <div className="text-center py-24 text-muted_ink">
          <p className="font-serif text-2xl italic">No books match your search.</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-8 md:gap-10">
          {books.map((b, i) => <BookCard key={b.id} book={b} index={i} purchasedCount={counts[b.id] || 0} />)}
        </div>
      )}
    </div>
  );
}
